import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const Wholesale = () => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: fd.get('name'),
      role: fd.get('role'),
      email: fd.get('email'),
      org: fd.get('org'),
      volume: fd.get('volume'),
      msg: fd.get('msg'),
    };
    setSending(true);
    try {
      await axios.post(`${API}/api/wholesale/leads`, payload);
      setSubmitted(true);
      toast.success('Request received', {
        description: `We'll be in touch with ${payload.name} within one business day.`,
      });
    } catch (err) {
      toast.error('Something went wrong', {
        description: 'Please try again, or email toronto@vegandanishbakery.com directly.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative py-24 md:py-32">
      <div className="container mx-auto">
        <div className="relative rounded-[2.5rem] gradient-leaf overflow-hidden shadow-pop">
          <div className="absolute inset-0 bg-noise opacity-[0.04]" />
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-12 p-8 sm:p-14 lg:p-20">
            <div className="lg:col-span-5 text-primary-foreground">
              <div className="font-script text-2xl text-accent">let's talk</div>
              <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight mt-1">
                Get the wholesale<br/>book in your inbox.
              </h2>
              <p className="mt-5 text-primary-foreground/80 max-w-md">
                Pricing tiers, certifications, nutritional spec, allergen sheets,
                and the campus playbook. One PDF. Sent within one business day.
              </p>

              <ul className="mt-8 space-y-3">
                {['Full 8-SKU pricing & MOQ matrix','Allergen + macro spec sheets','Campus pilot program guide','Sample box request (free)'].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-primary-foreground/90">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              {!submitted ? (
                <form onSubmit={onSubmit} className="bg-background/95 backdrop-blur rounded-3xl p-6 sm:p-8 space-y-5 shadow-pop">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-primary mb-1.5">Full name</Label>
                      <Input id="name" name="name" data-testid="wholesale-name-input" required placeholder="Maren Holt" className="h-11 rounded-xl" />
                    </div>
                    <div>
                      <Label htmlFor="role" className="text-primary mb-1.5">Role</Label>
                      <Input id="role" name="role" data-testid="wholesale-role-input" required placeholder="Director of Dining" className="h-11 rounded-xl" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-primary mb-1.5">Work email</Label>
                      <Input id="email" name="email" type="email" data-testid="wholesale-email-input" required placeholder="you@university.edu" className="h-11 rounded-xl" />
                    </div>
                    <div>
                      <Label htmlFor="org" className="text-primary mb-1.5">Organization</Label>
                      <Input id="org" name="org" data-testid="wholesale-org-input" required placeholder="University of Toronto" className="h-11 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-primary mb-1.5">Volume estimate (weekly)</Label>
                    <Select name="volume" defaultValue="5-15">
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="<5">Under 5 cases</SelectItem>
                        <SelectItem value="5-15">5 – 15 cases</SelectItem>
                        <SelectItem value="15-50">15 – 50 cases</SelectItem>
                        <SelectItem value="50+">50+ cases (pallet program)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="msg" className="text-primary mb-1.5">Tell us about your program</Label>
                    <Textarea id="msg" name="msg" data-testid="wholesale-msg-input" rows={4} placeholder="We operate 3 dining halls and 6 micro-markets…" className="rounded-xl" />
                  </div>
                  <Button type="submit" disabled={sending} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-base" data-testid="wholesale-form-submit">
                    {sending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>) : (<>Send me the wholesale book <ArrowRight className="ml-1.5 h-4 w-4" /></>)}
                  </Button>
                </form>
              ) : (
                <div className="bg-background/95 backdrop-blur rounded-3xl p-10 text-center shadow-pop">
                  <div className="h-16 w-16 mx-auto rounded-full gradient-honey grid place-items-center text-accent-foreground shadow-amber">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="font-display text-3xl text-primary font-medium mt-5">Wonderful — request received.</h3>
                  <p className="text-muted-foreground mt-3">
                    Watch your inbox for the wholesale book and a calendar link to chat with our campus team.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline" className="mt-6 rounded-full">
                    Send another
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Wholesale;
